/**
 * Exercice tutoriel de la feuille de brouillon (`/brouillon/tutoriel`).
 *
 * Un calcul que tout le monde sait faire (priorité des opérations), joué sur la feuille du moteur v2 (rien
 * d'enregistré, aucun HORA crédité), avec les gestes du prototype : recopier la ligne, toucher deux fois une
 * opération pour la sélectionner, choisir son résultat parmi les propositions du moteur, valider. Puis une réponse
 * en touches + « Check ». Le coach en haut de la page dit, à chaque instant, la seule chose à faire :
 * `tutorialStep` (pur, testé) la déduit de l'état de la feuille et du QCM.
 */

export const TUTORIAL_STATEMENT = 'Calcule A = 3 × 4 + 2 × 5.';

export const TUTORIAL_CHOICES = [
  { id: 'a', label: 'A = 22', line: 'A = 22', correct: true },
  { id: 'b', label: 'A = 70', line: 'A = 70', correct: false },
  { id: 'c', label: 'A = 42', line: 'A = 42', correct: false },
] as const;

/** Modèle du moteur v2 (format du noyau + niveau). */
export const TUTORIAL_MODEL = {
  level: '5e',
  symbols: { A: {} },
  goal: ['A'],
  kernel: {
    plugin: 'formula',
    start: 'A = 3 × 4 + 2 × 5',
    path: ['A = 12 + 10', 'A = 22'],
    answer: 'A = 22',
    goal: { kind: 'evaluate', target: 'A' },
  },
};

export type TutorialStepId =
  | 'touch'
  | 'copy'
  | 'select'
  | 'choose'
  | 'validate'
  | 'fix'
  | 'again'
  | 'answer'
  | 'end';

export interface TutorialInput {
  /** le clavier de la feuille est dans le dock */
  writing: boolean;
  /** lignes gardées (justes) */
  lines: number;
  /** HORA de la dernière ligne gardée */
  lastHora: number;
  /** la feuille a trouvé le résultat */
  done: boolean;
  /** la ligne en cours est vide */
  editorEmpty: boolean;
  /** la ligne en cours contient encore un « × » */
  hasTimes: boolean;
  /** des résultats sont proposés (une opération est sélectionnée) */
  proposing: boolean;
  /** la dernière ligne validée est fausse (🛑) */
  lastStopped: boolean;
  /** réponse envoyée par « Check » */
  checked: boolean;
}

export interface TutorialStep {
  id: TutorialStepId;
  /** numéro de l'étape (1 à 6), null à la fin */
  index: number | null;
  title: string;
  text: string;
}

export const TUTORIAL_STEP_COUNT = 6;

export function tutorialStep(s: TutorialInput): TutorialStep {
  if (s.checked) return { id: 'end', index: null, title: 'C’est fini !', text: '' };
  if (s.done)
    return {
      id: 'answer',
      index: 6,
      title: 'Choisis ta réponse',
      text: s.writing
        ? 'Touche « Répondre » en bas à gauche, choisis la réponse que ton brouillon a trouvée, puis Check.'
        : 'Touche la réponse que ton brouillon a trouvée, puis touche Check pour l’envoyer.',
    };
  if (!s.writing)
    return {
      id: 'touch',
      index: 1,
      title: 'Ouvre ton brouillon',
      text: 'Touche la ligne soulignée sous « départ » : le clavier de la feuille monte en bas de l’écran.',
    };
  if (s.lastStopped && !s.editorEmpty)
    return {
      id: 'fix',
      index: s.lines === 0 ? 4 : 5,
      title: 'Ligne fausse 🛑',
      text: 'Touche 🛑 dans la marge pour voir pourquoi. Ta ligne est restée en bas : touche le nombre faux, choisis le bon résultat, et valide.',
    };
  if (s.proposing)
    return {
      id: 'choose',
      index: 3,
      title: 'Choisis le résultat',
      text: 'Le moteur propose plusieurs résultats, un seul est juste. Touche celui de ce calcul : il remplace l’opération.',
    };
  if (s.lines === 0) {
    if (s.editorEmpty)
      return {
        id: 'copy',
        index: 2,
        title: 'Recopie la ligne de départ',
        text: 'Touche le bouton « copier » (deux feuilles) en haut du clavier : le départ s’écrit dans ta ligne.',
      };
    if (s.hasTimes)
      return {
        id: 'select',
        index: 3,
        title: 'Sélectionne une multiplication',
        text: 'Touche deux fois le signe × de 3 × 4 : l’opération est sélectionnée avec ses deux nombres.',
      };
    return {
      id: 'validate',
      index: 4,
      title: 'Valide ta ligne',
      text: 'Touche le ✓ bleu, en haut à droite du clavier : le moteur vérifie ta ligne.',
    };
  }
  if (s.editorEmpty)
    return {
      id: 'again',
      index: 5,
      title: s.lastHora > 0 ? `Ligne juste : +${s.lastHora} HORA` : 'Ligne juste',
      text: 'Une ligne juste et utile rapporte des HORA. Recopie-la, touche deux fois le +, choisis le résultat et valide.',
    };
  return {
    id: 'again',
    index: 5,
    title: 'Finis le calcul',
    text: 'Touche deux fois le + pour sélectionner 12 + 10, choisis le résultat, puis valide avec le ✓ bleu.',
  };
}

/**
 * Ce que le coach montre du doigt : l'élément à toucher pulse (`[data-coach-target]`). On ne lit
 * pas de consigne pour savoir où toucher — c'est ce qui rend le tutoriel (et la démo sans compte)
 * accessible du premier coup.
 */
export type CoachTarget =
  | 'editor'
  | 'copy'
  | 'op'
  | 'results'
  | 'validate'
  | 'mark'
  | 'reply'
  | 'keys'
  | 'check'
  | null;

export interface CoachTargetInput {
  step: TutorialStepId;
  /** le clavier de la feuille est dans le dock */
  writing: boolean;
  /** la ligne en cours est vide */
  editorEmpty: boolean;
  /** une touche de réponse est choisie */
  picked: boolean;
  /** la ligne en cours contient encore une opération à calculer */
  hasOp: boolean;
}

export function coachTarget({
  step,
  writing,
  editorEmpty,
  picked,
  hasOp,
}: CoachTargetInput): CoachTarget {
  switch (step) {
    case 'touch':
      return 'editor';
    case 'copy':
      return 'copy';
    case 'select':
      return 'op';
    case 'choose':
      return 'results';
    case 'validate':
      return 'validate';
    case 'fix':
      return 'mark';
    case 'again':
      if (editorEmpty) return 'copy';
      return hasOp ? 'op' : 'validate';
    case 'answer':
      if (writing) return 'reply';
      return picked ? 'check' : 'keys';
    default:
      return null;
  }
}

/** Opération que le coach fait sélectionner : le premier × tant qu'il en reste, sinon le premier +. */
export function coachOperator(tokens: readonly { k: string; v?: string }[]): number | null {
  const times = tokens.findIndex((t) => t.k === 'op' && t.v === '×');
  if (times >= 0) return times;
  const plus = tokens.findIndex((t) => t.k === 'op' && t.v === '+');
  return plus >= 0 ? plus : null;
}

/** Sélecteur CSS de l'élément montré par le coach (dans la page du tutoriel). PUR. */
export function coachSelector(target: CoachTarget, opIndex: number | null): string | null {
  switch (target) {
    case 'editor':
      return '[data-testid="bv2-editor"]';
    case 'copy':
      return '[data-testid="bv2-copy"]';
    case 'op':
      return opIndex === null
        ? null
        : `[data-testid="bv2-editor"] [data-c="[]"][data-i="${opIndex}"]`;
    case 'results':
      return '[data-testid="bv2-tool-result"]';
    case 'validate':
      return '[data-testid="bv2-validate"]';
    case 'mark':
      return '.bv-mark.stop';
    case 'reply':
      return '[data-testid="bv2-keyboard"] .bv-answer';
    case 'keys':
      return '[data-testid="dock-answer-row"]';
    case 'check':
      return '[data-testid="dock-check"]';
    default:
      return null;
  }
}

const DONE_KEY = 'jr_scratch_tutorial_done';

/** Tutoriel déjà fait sur cet appareil ? (stockage inaccessible : on ne le propose pas) */
export function hasDoneScratchTutorial(): boolean {
  try {
    return localStorage.getItem(DONE_KEY) === '1';
  } catch {
    return true;
  }
}

export function markScratchTutorialDone(): void {
  try {
    localStorage.setItem(DONE_KEY, '1');
  } catch {
    /* stockage indisponible */
  }
}
